import { useRef, useEffect, useCallback } from 'react';

const SENSITIVITY   = 25;   // diff pixel threshold (0-255)
const MOTION_RATIO  = 0.015; // 1.5% de pixels changés = mouvement
const COOLDOWN_MS   = 8000;  // min 8s entre deux alertes

export function useMotionDetection({ videoRef, enabled, onMotion }) {
  const canvasRef     = useRef(null);
  const prevFrameRef  = useRef(null);
  const lastAlertRef  = useRef(0);
  const rafRef        = useRef(null);
  const activeRef     = useRef(false);

  const captureSnapshot = useCallback((canvas, ctx) => {
    const dataUrl = canvas.toDataURL('image/jpeg', 0.6);
    return dataUrl;
  }, []);

  const analyze = useCallback(() => {
    if (!activeRef.current) return;
    const video = videoRef.current;
    if (!video || video.readyState < 2) { rafRef.current = requestAnimationFrame(analyze); return; }

    if (!canvasRef.current) {
      canvasRef.current = document.createElement('canvas');
      canvasRef.current.width  = 160;
      canvasRef.current.height = 90;
    }
    const canvas = canvasRef.current;
    const ctx    = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(video, 0, 0, 160, 90);
    const frame = ctx.getImageData(0, 0, 160, 90);

    if (prevFrameRef.current) {
      const prev    = prevFrameRef.current.data;
      const curr    = frame.data;
      let changed   = 0;
      const total   = 160 * 90;

      for (let i = 0; i < curr.length; i += 4) {
        const dr = Math.abs(curr[i]   - prev[i]);
        const dg = Math.abs(curr[i+1] - prev[i+1]);
        const db = Math.abs(curr[i+2] - prev[i+2]);
        if ((dr + dg + db) / 3 > SENSITIVITY) changed++;
      }

      const ratio = changed / total;
      if (ratio > MOTION_RATIO) {
        const now = Date.now();
        if (now - lastAlertRef.current > COOLDOWN_MS) {
          lastAlertRef.current = now;
          const thumbnail = captureSnapshot(canvas, ctx);
          onMotion?.({ ratio: Math.round(ratio * 100), thumbnail });
        }
      }
    }

    prevFrameRef.current = frame;
    rafRef.current = requestAnimationFrame(analyze);
  }, [videoRef, onMotion, captureSnapshot]);

  useEffect(() => {
    if (enabled) {
      activeRef.current = true;
      rafRef.current    = requestAnimationFrame(analyze);
    } else {
      activeRef.current    = false;
      prevFrameRef.current = null;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    }
    return () => {
      activeRef.current = false;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [enabled, analyze]);
}
